import React, { type ReactNode, useMemo } from 'react';

import { ConfluenceIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { ConversationStartersBanner } from '@atlassian/search-ai/conversation-starters-banner';
import { getSAINLayerFrontendExperiment } from '@atlassian/search-ai/get-sain-layer-frontend-experiment';
import {
	getProductAndIDFromARI,
	isSearchConfluencePageBlogAttachment,
	isSearchConfluenceSpace,
	type SearchConfluencePartial,
	SearchConfluenceSubtype,
} from '@atlassian/search-client';
import { getConfluenceUrlWithAnalyticsQueryParams } from '@atlassian/search-common';

import { ApplicationModes } from '../../../../../../common/constants/application-modes';
import { ProductKeys } from '../../../../../../common/constants/products';
import { CommentCount } from '../../../../../../common/ui/comment-count';
import { ConfluenceResultMeta } from '../../../../../../common/ui/confluence-result-meta';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import {
	type ActivityEventType,
	EventTypeMessage,
} from '../../../../../../common/ui/event-type-message';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { LikeCount } from '../../../../../../common/ui/like-count';
import {
	MentionsLinkedResults,
	SimilarLinkedResults,
} from '../../../../../../common/ui/linked-results';
import { getConfluenceEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultSiteName } from '../../../../../../common/ui/search-result-site-name';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { BreadcrumbSocialSignal } from '../../../../../../common/ui/social-signals';
import { Verified } from '../../../../../../common/ui/verified';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useCommonAndPrivateAttributes } from '../../../../../../controllers/analytics';
import { useAreLLMPoweredFeaturesEnabled } from '../../../../../../controllers/entitlement-checks';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap, useMultiSiteSearchInRovo, useSearchSessionId, useUserShardUI } from '../../../../../../controllers/store/bootstrap';
import { useFeatures, usePrimaryProduct } from '../../../../../../controllers/store/bootstrap/accessors';
import { useSearchQuery, useSelectedProduct } from '../../../../../../controllers/store/search';

const getTitle = (partial: SearchConfluencePartial) => {
	return enrichExcerpt(partial.title);
};

const getLastModified = (partial: SearchConfluencePartial, serveUserShardUI: boolean) => {
	// Check if user action data is available and feature flag is enabled
	if (serveUserShardUI && partial.latestUserAction && partial.latestUserActionTs) {
		return (
			<EventTypeMessage
				eventType={partial.latestUserAction as ActivityEventType}
				timestamp={partial.latestUserActionTs}
				isMyActivityData={true}
			/>
		);
	}

	// Fall back to lastModified if available
	if (partial.lastModified) {
		return <LastModified lastModified={partial.lastModified} />;
	}

	return null;
};

const getBreadcrumbs = (partial: SearchConfluencePartial) => {
	if (!isSearchConfluencePageBlogAttachment(partial)) {
		return null;
	}

	const name = partial.space?.name;
	if (!name) {
		return null;
	}

	return (
		<ConfluenceResultMeta name={name} url={partial.url} webUiLink={partial.space?.webUiLink} />
	);
};

const getVerifiedIcon = (partial: SearchConfluencePartial) => {
	if (isSearchConfluencePageBlogAttachment(partial) && partial.isVerified) {
		return <Verified />;
	}
	return null;
};

const getCommentCount = (partial: SearchConfluencePartial) => {
	if (!isSearchConfluencePageBlogAttachment(partial)) {
		return null;
	}

	const numberOfComments = partial.confluenceEntity?.commentCountSummary?.total ?? 0;

	if (numberOfComments === 0) {
		return null;
	}

	return <CommentCount numberOfComments={numberOfComments} />;
};

const getLikeCount = (partial: SearchConfluencePartial) => {
	if (!isSearchConfluencePageBlogAttachment(partial)) {
		return null;
	}

	const numberOfLikes = partial.confluenceEntity?.likesSummary?.count ?? 0;

	if (numberOfLikes === 0) {
		return null;
	}

	return <LikeCount numberOfLikes={numberOfLikes} />;
};

const getDescription = (partial: SearchConfluencePartial) => {
	return processExcerpt(partial.description);
};

interface ConfluenceSearchResultProps {
	partial: SearchConfluencePartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const ConfluenceSearchResult = ({
	partial,
	position,
}: ConfluenceSearchResultProps): React.JSX.Element => {
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const [searchSessionId] = useSearchSessionId();
	const [primaryProduct] = usePrimaryProduct();
	const [features] = useFeatures();
	const [query] = useSearchQuery();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const applicationMode = useApplicationMode();
	const multiSiteSearchInRovo = useMultiSiteSearchInRovo();
	const userShardUI = useUserShardUI();
	const areLLMPoweredFeaturesEnabled = useAreLLMPoweredFeaturesEnabled();
	const is3pGroupingEnabled = fg('fps_3p_grouping_rollout_gate');

	const isSideBySideEnabled = features?.sideBySide?.enabled;

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(partial.id);
		let containerId: string | null = null;
		if (isSearchConfluencePageBlogAttachment(partial) && partial.space?.id) {
			const [, , , spaceId] = getProductAndIDFromARI(partial.space.id);
			containerId = spaceId;
		}

		return {
			id: partial.id,
			containerId,
			spaceId: containerId,
			contentType: partial.type,
			entityType: partial.type,
			contentId,
			product,
			isSideBySideEnabled,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [partial, isSideBySideEnabled]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
		socialSignal: partial.socialSignal?.signal || undefined,
	});

	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
		socialSignal: partial.socialSignal?.signal || undefined,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const subtype = isSearchConfluencePageBlogAttachment(partial)
		? partial.confluenceEntity?.subtype
		: undefined;

	const metadataParts: [string, ReactNode][] = [
		['site-name', multiSiteSearchInRovo ? <SearchResultSiteName cloudARI={partial.id} /> : null],
		['type', <SearchResultType type={partial.type} subtype={subtype} />],
		['breadcrumbs', getBreadcrumbs(partial)],
		['last-modified', getLastModified(partial, userShardUI)],
	];

	const socialMetadataParts: [string, ReactNode][] = [
		['comment-count', getCommentCount(partial)],
		['like-count', getLikeCount(partial)],
	];

	const titleMetadataParts: [string, ReactNode][] = [['verified', getVerifiedIcon(partial)]];

	if (
		partial.socialSignal &&
		applicationMode === ApplicationModes.Rovo &&
		(fg('search-plex-jira-social-signal') || !isSearchConfluenceSpace(partial))
	) {
		socialMetadataParts.push([
			'social-signal',
			<BreadcrumbSocialSignal partial={partial} commonAttributes={commonAttributes} />,
		]);
	}

	const hasSimilarResults = partial.linkedResults?.some(
		(linkedResult) => linkedResult.category?.toUpperCase() === 'SIMILAR',
	);

	const { serveExperiment: serveConversationStartersExperiment } = getSAINLayerFrontendExperiment({
		abTestId: 'kd_convo_starters_in_search',
	});

	const isConversationStartersEnabled = fg('kd_convo_starters_dogfooding');

	const conversationStartersIfEnabled =
		applicationMode === ApplicationModes.Rovo &&
			!!(serveConversationStartersExperiment || isConversationStartersEnabled) &&
			position === 0 &&
			areLLMPoweredFeaturesEnabled &&
			['page', 'blogpost'].includes(partial.type)
			? {
				ari: partial.id,
				url: getConfluenceUrlWithAnalyticsQueryParams({
					url: partial.url,
					query,
					searchSessionId,
				}),
			}
			: null;

	let icon: React.JSX.Element;
	if (fg('rovo_search_confluence_icons_fps')) {
		const isLiveDoc =
			isSearchConfluencePageBlogAttachment(partial) &&
			partial.confluenceEntity?.subtype === SearchConfluenceSubtype.LIVE;

		icon = getConfluenceEntityIcon({
			iconUrl: partial.iconUrl,
			type: partial.type,
			size: 'medium',
			isLiveDoc,
		});
	} else {
		icon = <ConfluenceIcon size="medium" shouldUseNewLogoDesign />;
	}

	return (
		<SearchResult
			{...partial}
			product={ProductKeys.Confluence}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={getConfluenceUrlWithAnalyticsQueryParams({
				url: partial.url,
				query,
				searchSessionId,
			})}
			title={getTitle(partial)}
			icon={icon}
			headerMetadataParts={metadataParts}
			headerMetadataSocialParts={socialMetadataParts}
			titleMetadata={<SearchMetadata parts={titleMetadataParts} displaySpacer={false} />}
			description={<DescriptionBody description={getDescription(partial)} />}
			hideSummarizeInChat={!!conversationStartersIfEnabled}
			cloudId={cloudId}
			position={position}
			partial={partial}
			appMode={applicationMode}
			footer={
				cloudId && conversationStartersIfEnabled ? (
					<ConversationStartersBanner
						primaryProduct={primaryProduct}
						cloudId={cloudId}
						searchResult={conversationStartersIfEnabled}
						analyticsAttributes={{ resultMetadata: metadata, ...commonAttributes }}
					/>
				) : hasSimilarResults ? (
					<SimilarLinkedResults
						linkedResults={partial.linkedResults || []}
						thirdPartyConfigs={thirdPartyConfigs}
						partial={partial}
						url={getConfluenceUrlWithAnalyticsQueryParams({
							url: partial.url,
							query,
							searchSessionId,
						})}
						position={position}
						onResultClicked={onResultClicked}
						isAllProductSelected={!selectedProduct}
						onResultContextMenu={onResultCtxMenuClicked}
						is3pGroupingEnabled={is3pGroupingEnabled}
					/>
				) : (
					<MentionsLinkedResults
						linkedResults={partial.linkedResults || []}
						thirdPartyConfigs={thirdPartyConfigs}
						partial={partial}
						url={getConfluenceUrlWithAnalyticsQueryParams({
							url: partial.url,
							query,
							searchSessionId,
						})}
						position={position}
						onResultClicked={onResultClicked}
						onResultContextMenu={onResultCtxMenuClicked}
					/>
				)
			}
		/>
	);
};
